import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  Progress,
} from "@mantine/core";
import { motion } from "framer-motion";
import {
  IconVideo,
  IconPlayerPlay,
  IconPlayerPause,
  IconVolume,
  IconVolumeOff,
  IconMaximize,
  IconSettings,
  IconList,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import MegaLoader from "../../Components/MegaLoader";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { RecordingProps } from "../../Lib/types";
import { Endpoints } from "../../Lib/endpoints";
import "./styles.scss";
import { Container } from "@mui/material";

const SingleRecording: React.FC = () => {
  const { recordingID } = useParams();
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [theatreMode, setTheatreMode] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { data: recording, isLoading: isLoadingRecording } = usePerformRequest<
    NonPaginatedResponse<RecordingProps>
  >({
    method: Endpoints.GetSingleRecording(recordingID as string).method,
    route: Endpoints.GetSingleRecording(recordingID as string).route,
  });

  const files = recording?.data?.files || [];
  const currentFile = files[currentIndex];

  const handlePlaylistItemClick = (index: number) => {
    setCurrentIndex(index);
    setPlaying(true);
    setCurrentTime(0);
  };

  const togglePlaylist = () => {
    setShowPlaylist(!showPlaylist);
  };

  const toggleTheatreMode = () => {
    setTheatreMode(!theatreMode);
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const handleVideoEnded = () => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setPlaying(true);
      setCurrentTime(0);
    } else {
      setPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const progressWidth = rect.width;
      const clickPosition = clickX / progressWidth;
      const newTime = clickPosition * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {isLoadingRecording ? (
        <MegaLoader />
      ) : (
        <div
          className={`single-recording-container ${
            theatreMode ? "theatre-mode" : ""
          }`}
        >
          {files.length > 0 ? (
            <div className="video-player-wrapper">
              {/* Main Video Player */}
              <div className="main-player-section">
                <div className="video-container">
                  <video
                    ref={videoRef}
                    src={currentFile}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                    onEnded={handleVideoEnded}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    muted={muted}
                  />

                  {/* Custom Controls Overlay */}
                  <div className="video-controls-overlay">
                    {/* Progress Bar */}
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar"
                        onClick={handleProgressClick}
                      >
                        <Progress
                          value={progressPercentage}
                          color="blue"
                          size="sm"
                          className="progress-fill"
                        />
                      </div>
                    </div>

                    <div className="controls-bottom">
                      <Group justify="space-between" p="md">
                        <Group gap="xs">
                          <Button
                            variant="subtle"
                            color="white"
                            size="sm"
                            onClick={togglePlayPause}
                          >
                            {playing ? (
                              <IconPlayerPause size={16} />
                            ) : (
                              <IconPlayerPlay size={16} />
                            )}
                          </Button>
                          <Button
                            variant="subtle"
                            color="white"
                            size="sm"
                            onClick={toggleMute}
                          >
                            {muted ? (
                              <IconVolumeOff size={16} />
                            ) : (
                              <IconVolume size={16} />
                            )}
                          </Button>
                          <Text c="white" fw={600} size="sm">
                            {formatTime(currentTime)} / {formatTime(duration)}
                          </Text>
                          <Text c="white" fw={600} size="sm">
                            Video {currentIndex + 1} of {files.length}
                          </Text>
                        </Group>

                        <Group gap="xs">
                          <Button
                            variant="subtle"
                            color="white"
                            size="sm"
                            onClick={toggleTheatreMode}
                          >
                            <IconMaximize size={16} />
                          </Button>
                          <Button
                            variant="subtle"
                            color="white"
                            size="sm"
                            onClick={togglePlaylist}
                          >
                            <IconList size={16} />
                          </Button>
                        </Group>
                      </Group>
                    </div>
                  </div>
                </div>
              </div>

              {/* Playlist Section */}
              {showPlaylist && (
                <motion.div
                  initial={{ opacity: 0, x: 300 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 300 }}
                  className="playlist-section"
                >
                  <div className="playlist-header">
                    <Text fw={700} fz={18} mb={12}>
                      Playlist ({files.length} videos)
                    </Text>
                  </div>

                  <div className="playlist-list">
                    {files.map((file, index) => (
                      <motion.div
                        key={file}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card
                          className={`playlist-item ${
                            index === currentIndex ? "active" : ""
                          }`}
                          onClick={() => handlePlaylistItemClick(index)}
                          p="sm"
                          mb="xs"
                          style={{ cursor: "pointer" }}
                        >
                          <Group gap="sm" align="center">
                            <div className="playlist-thumbnail">
                              <div className="thumbnail-placeholder">
                                <IconVideo size={20} />
                              </div>
                              {index === currentIndex && playing && (
                                <div className="playing-indicator">
                                  <IconPlayerPlay size={12} />
                                </div>
                              )}
                            </div>

                            <div
                              className="playlist-item-content"
                              style={{ flex: 1 }}
                            >
                              <Text fw={600} size="sm" lineClamp={1}>
                                Video {index + 1}
                              </Text>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {file.split("/").pop() || "Video file"}
                              </Text>
                            </div>

                            <Badge
                              size="xs"
                              variant={
                                index === currentIndex ? "filled" : "light"
                              }
                              color={index === currentIndex ? "blue" : "gray"}
                            >
                              {index + 1}
                            </Badge>
                          </Group>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          ) : (
            <Container maxWidth="lg">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Alert
                  icon={<IconVideo size={20} />}
                  title="No Videos Available"
                  color="blue"
                  variant="light"
                  className="no-recordings-alert"
                >
                  <Text>No video files found for this recording.</Text>
                </Alert>
              </motion.div>
            </Container>
          )}
        </div>
      )}
    </>
  );
};

export default SingleRecording;
